import { Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { Car, Package, House, Bell, Buildings, ArrowRight, Sparkle, Phone, EnvelopeSimple, MapPin } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";

const HERO_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_ridexelite-platform/artifacts/si7abj9b_20260723_012814-ANIMATION.gif";
const HERO_HANDSHAKE_IMG = "https://customer-assets-rejwkqb3.emergentagent.net/job_ridexelite-platform/artifacts/z5g40i9p_handshake_final_cutout.webp";

const VERTICALS = [
  { id: "transport", name: "Transport & Delivery", tag: "Rides, packages, food & errands", color: "#1E3A8A", Icon: Car, blurb: "Chauffeured rides, airport transfers, hourly service, same-day couriers, express food runs.", img: "https://images.unsplash.com/photo-1627285886624-5cd637dafb50" },
  { id: "home", name: "Home", tag: "Cleaning, repairs, maintenance", color: "#9A3412", Icon: House, blurb: "Deep cleans, handyman visits, scheduled maintenance.", img: "https://images.unsplash.com/photo-1628745277862-bc0b2d68c50c" },
  { id: "concierge", name: "Concierge", tag: "Personal assistance & errands", color: "#4C1D95", Icon: Bell, blurb: "Personal shopping, travel planning, event coordination.", img: "https://images.pexels.com/photos/7820689/pexels-photo-7820689.jpeg" },
  { id: "business", name: "Business", tag: "Office services & logistics", color: "#334155", Icon: Buildings, blurb: "Corporate couriers, logistics ops, office cleaning.", img: "https://images.pexels.com/photos/13219418/pexels-photo-13219418.jpeg" },
];

const TICKER_ITEMS = [
  { service: "Airport transfer", city: "Monrovia", ago: "3 min ago" },
  { service: "Deep clean", city: "Philadelphia", ago: "6 min ago" },
  { service: "Same-day courier", city: "Marshall", ago: "9 min ago" },
  { service: "Executive ride", city: "Newark", ago: "12 min ago" },
  { service: "Handyman visit", city: "Paynesville", ago: "15 min ago" },
  { service: "Discover Liberia tour", city: "Monrovia", ago: "18 min ago" },
  { service: "Office logistics", city: "Trenton", ago: "22 min ago" },
  { service: "Personal concierge", city: "Sinkor", ago: "26 min ago" },
];

const GIFT_FALLBACK = [
  { name: "Kwame", city: "Boston", service: "Deep Cleaning", ago: "4h ago" },
  { name: "Yasmine", city: "Toronto", service: "Wedding Day Prep Bundle", ago: "1d ago" },
  { name: "Amina", city: "Newark", service: "Airport Arrival Chauffeur", ago: "2h ago" },
  { name: "Emeka", city: "Atlanta", service: "Deep Cleaning", ago: "6h ago" },
  { name: "Fatou", city: "Philadelphia", service: "Beauty Day Bundle", ago: "9h ago" },
];

function GiftTicker() {
  const [items, setItems] = useState(GIFT_FALLBACK);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/gifts/recent-public`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (!alive) return;
        const real = Array.isArray(data?.items) ? data.items : [];
        // Merge real + fallback so the rotation always feels alive, but real items lead.
        const merged = real.length >= 3 ? real : [...real, ...GIFT_FALLBACK].slice(0, 8);
        setItems(merged);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 3600);
    return () => clearInterval(t);
  }, [items.length]);

  const it = items[idx % items.length];
  return (
    <div
      data-testid="gift-ticker"
      className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-black/50 backdrop-blur-md mt-4"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-[#D4AF37] opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-[#D4AF37]" />
      </span>
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">See who's gifting</span>
      <div className="relative h-4 overflow-hidden min-w-[300px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center gap-2 text-xs text-zinc-200 whitespace-nowrap"
          >
            <span className="text-white font-medium">{it.name}</span>
            <span className="text-zinc-500">in</span>
            <span className="text-[#D4AF37]">{it.city}</span>
            <span className="text-zinc-500">gifted</span>
            <span className="text-white">{it.service}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-500">{it.ago}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

const GIFT_IDEAS = [
  {
    id: "moms_deep_clean",
    vertical: "home",
    lookupKey: "home_cleaning",
    recipient: "For Mom",
    title: "Monthly Deep Clean",
    blurb: "Send your mother a full home deep-clean in Sinkor or Paynesville. Vetted crew, gold-standard hygiene, spotless from kitchen to laundry.",
    price: 55,
    emoji: "🏠",
    accent: "#9A3412",
  },
  {
    id: "sister_wedding_prep",
    vertical: "concierge",
    lookupKey: "concierge_bundle_wedding_prep",
    recipient: "For Sister · Bride",
    title: "Wedding Day Prep Bundle",
    blurb: "The full bridal team on wedding morning — Hairstylist + Makeup Artist + Nails + Groom's Barber. Coordinated. Vetted. Unforgettable.",
    price: 130,
    emoji: "💍",
    accent: "#D4AF37",
  },
  {
    id: "dad_airport_ride",
    vertical: "transport",
    lookupKey: "transport_airport_shuttle",
    recipient: "For Dad",
    title: "Airport Arrival Chauffeur",
    blurb: "Executive pickup at Roberts International with the family driver waiting at arrivals. Water, phone charger, and a smooth ride home to Congo Town.",
    price: 45,
    emoji: "🛬",
    accent: "#1E3A8A",
  },
];

const API = process.env.REACT_APP_BACKEND_URL;

// Short captions for the rotating hero polaroid (one per GIFT_IDEAS preset, keyed by id).
const HERO_POLAROID_CAPTIONS = {
  moms_deep_clean: { sendLabel: "Send Mom", subline: "Deep clean · Sinkor" },
  sister_wedding_prep: { sendLabel: "Send Sister", subline: "Wedding day team" },
  dad_airport_ride: { sendLabel: "Send Dad", subline: "Airport chauffeur" },
};

function RotatingGiftPolaroid() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % GIFT_IDEAS.length), 6000);
    return () => clearInterval(t);
  }, []);
  const preset = GIFT_IDEAS[idx];
  const cap = HERO_POLAROID_CAPTIONS[preset.id] || { sendLabel: "Send home", subline: preset.title };
  const href = `/book/${preset.vertical}/${preset.lookupKey}?gift=1`;

  return (
    <motion.div
      initial={{ opacity: 0, y: 24, rotate: -6 }}
      animate={{ opacity: 1, y: 0, rotate: -4 }}
      transition={{ duration: 1.0, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
      className="w-40 sm:w-44 shrink-0"
    >
      <Link
        to={href}
        data-testid="hero-diaspora-accent"
        data-sound="coin"
        aria-label={`Gift ${preset.title} to your family in Monrovia`}
        className="block group rounded-[1.25rem] overflow-hidden border-2 border-[#D4AF37]/70 shadow-[0_20px_60px_-15px_rgba(212,175,55,0.5)] bg-[#0B0B0D] hover:border-[#D4AF37] hover:shadow-[0_25px_80px_-15px_rgba(212,175,55,0.7)] hover:-translate-y-1 hover:rotate-[-2deg] transition-all duration-300"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          {/* Animated background stays constant — the diaspora joy footage */}
          <img
            src={HERO_IMG}
            alt="Diaspora joy — home to Liberia"
            className="absolute inset-0 w-full h-full object-cover object-center"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/10 to-black/20" />
          <div className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#D4AF37] text-black text-[8px] font-semibold uppercase tracking-[0.15em]">
            Gift · Diaspora
          </div>
          {/* Recipient chip rotates */}
          <AnimatePresence mode="wait">
            <motion.div
              key={preset.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
              className="absolute bottom-2 left-2 right-2 flex items-center gap-1.5"
            >
              <span className="text-base leading-none" aria-hidden>{preset.emoji}</span>
              <span className="text-[9px] uppercase tracking-[0.2em] text-white/90 truncate">{preset.recipient}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        {/* Caption block rotates */}
        <div className="px-3 py-2 bg-black/85 backdrop-blur-md flex items-center justify-between gap-2 min-h-[42px]">
          <div className="min-w-0 flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={preset.id}
                initial={{ opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -6 }}
                transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              >
                <div className="text-[9px] uppercase tracking-[0.25em] text-[#D4AF37]">{cap.sendLabel}</div>
                <div className="text-[10px] text-zinc-200 leading-tight mt-0.5 truncate">{cap.subline} →</div>
              </motion.div>
            </AnimatePresence>
          </div>
          <ArrowRight size={12} weight="bold" className="text-[#D4AF37] shrink-0 group-hover:translate-x-0.5 transition-transform" />
        </div>
        {/* 3-dot indicator */}
        <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1">
          {GIFT_IDEAS.map((_, i) => (
            <span
              key={i}
              className={`h-1 rounded-full transition-all duration-500 ${
                i === idx ? "w-4 bg-[#D4AF37]" : "w-1.5 bg-white/25"
              }`}
            />
          ))}
        </div>
      </Link>
    </motion.div>
  );
}

function LiveBookingTicker() {
  const [items, setItems] = useState(TICKER_ITEMS);
  const [idx, setIdx] = useState(0);

  useEffect(() => {
    let alive = true;
    fetch(`${API}/api/bookings/recent-public`)
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (alive && data?.items?.length > 0) setItems(data.items);
      })
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setIdx(i => (i + 1) % items.length), 3200);
    return () => clearInterval(t);
  }, [items.length]);

  const item = items[idx % items.length];
  return (
    <div
      data-testid="live-booking-ticker"
      className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-[#D4AF37]/25 bg-black/50 backdrop-blur-md"
    >
      <span className="relative flex h-2 w-2">
        <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
        <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
      </span>
      <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-400">Now booking</span>
      <div className="relative h-4 overflow-hidden min-w-[260px]">
        <AnimatePresence mode="wait">
          <motion.div
            key={idx}
            initial={{ y: 14, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -14, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            className="absolute inset-0 flex items-center gap-2 text-xs text-zinc-200 whitespace-nowrap"
          >
            <span className="text-white font-medium">{item.service}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-[#D4AF37]">{item.city}</span>
            <span className="text-zinc-500">·</span>
            <span className="text-zinc-500">{item.ago}</span>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

export default function Landing() {
  return (
    <div data-testid="landing-page">
      {/* HERO */}
      <section className="relative overflow-hidden hero-radial">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 lg:pt-28 pb-16">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
            data-testid="hero-composition"
            className="relative rounded-[2.5rem] overflow-hidden border border-[#D4AF37]/30 shadow-[0_30px_120px_-40px_rgba(212,175,55,0.4)]"
          >
            {/* Cinematic split hero: emblem left · editorial + CTAs right */}
            <div className="relative bg-[#09090B] overflow-hidden">
              {/* radial gold glow */}
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "radial-gradient(50% 70% at 30% 45%, rgba(212,175,55,0.22) 0%, rgba(212,175,55,0.05) 40%, transparent 70%)",
                }}
              />
              {/* faint gold rays */}
              <div
                className="absolute inset-0 opacity-30 mix-blend-screen"
                style={{
                  background:
                    "conic-gradient(from 90deg at 30% 100%, rgba(212,175,55,0) 0deg, rgba(212,175,55,0.3) 40deg, rgba(212,175,55,0) 80deg, rgba(212,175,55,0) 280deg, rgba(212,175,55,0.3) 320deg, rgba(212,175,55,0) 360deg)",
                }}
              />
              {/* grain */}
              <div className="grain-overlay opacity-40" />

              {/* corner brand plates */}
              <div className="absolute top-5 left-5 text-[10px] uppercase tracking-[0.3em] text-zinc-500 z-10">est. 2025</div>
              <div className="absolute top-5 right-5 text-[10px] uppercase tracking-[0.3em] text-zinc-500 z-10">USA · Liberia</div>
              <div className="absolute bottom-5 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.35em] text-[#D4AF37]/70 z-10 hidden sm:block">One Platform · Every Service</div>

              {/* Split grid */}
              <div className="relative z-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-center px-6 py-14 sm:px-10 md:px-14 lg:px-16">
                {/* LEFT · Official RX gold emblem */}
                <motion.div
                  data-testid="hero-handshake-centerpiece"
                  initial={{ opacity: 0, scale: 0.9, y: -6 }}
                  animate={{ opacity: 1, scale: 1, y: 0 }}
                  transition={{ duration: 1.1, delay: 0.1, ease: [0.22, 1, 0.36, 1] }}
                  className="mx-auto w-56 h-56 sm:w-72 sm:h-72 md:w-[24rem] md:h-[24rem] lg:w-[28rem] lg:h-[28rem]"
                  style={{
                    filter: "drop-shadow(0 30px 70px rgba(212,175,55,0.4)) drop-shadow(0 0 25px rgba(212,175,55,0.18))",
                  }}
                >
                  <img
                    src="/brand/ridexelite_logo.jpg"
                    alt="RideXelite Services LLC — Driven by Excellence, Built on Trust"
                    className="w-full h-full object-contain"
                    style={{ mixBlendMode: "screen" }}
                  />
                </motion.div>

                {/* RIGHT · Editorial statement + primary CTAs */}
                <motion.div
                  data-testid="hero-editorial"
                  initial={{ opacity: 0, x: 24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.9, delay: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="text-left md:pr-4"
                >
                  <div className="mb-5">
                    <LiveBookingTicker />
                  </div>
                  <div className="eyebrow mb-4 flex items-center gap-2">
                    <Sparkle weight="fill" size={12} className="text-[#D4AF37]" />
                    RideXelite Services LLC · Parent Platform
                  </div>
                  <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02] font-medium text-white">
                    One elite platform.<br />
                    <span className="gold-text italic">Four</span> verticals.<br />
                    <span className="gold-text italic">Two</span> continents.
                  </h1>
                  <p className="mt-5 text-base sm:text-lg text-zinc-300 max-w-xl leading-relaxed">
                    Transport &amp; Delivery, Home, Concierge and Business — book on demand in the
                    USA and Liberia. Driven by Excellence, Built on Trust.
                  </p>
                  <div className="mt-8 flex flex-col sm:flex-row gap-4">
                    <Link
                      to="/services/transport"
                      data-testid="hero-book-service-btn"
                      data-sound="tock"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#F3E5AB] transition-colors"
                    >
                      Book now <ArrowRight size={16} weight="bold" />
                    </Link>
                    <Link
                      to="/become-provider"
                      data-testid="hero-become-provider-btn"
                      data-sound="tock"
                      className="inline-flex items-center justify-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white text-sm font-medium hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
                    >
                      Become a provider
                    </Link>
                  </div>
                </motion.div>
              </div>
            </div>
            <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-[#09090B] via-[#09090B]/50 to-transparent pointer-events-none" />
          </motion.div>

          {/* 5-VERTICAL QUICK-LINK STRIP — merges the centerpiece image with the service verticals */}
          <motion.div
            data-testid="hero-verticals-strip"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative -mt-10 lg:-mt-14 mx-4 lg:mx-16 rounded-[1.75rem] border border-[#D4AF37]/30 bg-[#0B0B0D]/85 backdrop-blur-xl shadow-[0_20px_60px_-20px_rgba(0,0,0,0.6)] px-4 sm:px-6 py-5"
          >
            <div className="flex items-center justify-between gap-4 mb-4">
              <div className="eyebrow gold-text">RideXelite Services · 4 Verticals</div>
              <div className="hidden sm:block text-xs text-zinc-500 tracking-[0.2em] uppercase">Tap to explore</div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3">
              {VERTICALS.map((v) => (
                <Link
                  key={v.id}
                  to={`/services/${v.id}`}
                  data-testid={`hero-vertical-link-${v.id}`}
                  className="group flex items-center gap-3 rounded-2xl border border-white/10 bg-black/30 hover:bg-black/50 hover:border-[#D4AF37]/50 transition-colors px-3 sm:px-4 py-3"
                >
                  <div
                    className="w-9 h-9 shrink-0 rounded-xl flex items-center justify-center"
                    style={{ background: `${v.color}30`, border: `1px solid ${v.color}` }}
                  >
                    <v.Icon weight="duotone" size={18} className="text-white" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-white text-sm font-medium truncate group-hover:text-[#D4AF37] transition-colors">{v.name}</div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500 truncate">{v.tag}</div>
                  </div>
                </Link>
              ))}
            </div>
          </motion.div>

          <div className="mt-16" data-testid="hero-stats-row">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="rounded-2xl border border-white/10 bg-[#121214] p-5">
                <div className="font-serif-display text-3xl text-white">4</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">Verticals</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-[#121214] p-5">
                <div className="font-serif-display text-3xl text-white">24/7</div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">Booking</div>
              </div>
              <div className="rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5">
                <div className="font-serif-display text-3xl text-white">80<span className="gold-text">%</span></div>
                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">Provider payout</div>
              </div>
              <div className="rounded-2xl border border-white/10 bg-black/40 p-5">
                <div className="eyebrow gold-text">Available</div>
                <div className="font-serif-display text-base text-white mt-1 leading-tight">Daily · Weekly · Monthly Rentals</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* PARENT PLATFORM OVERVIEW */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="parent-overview-section">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-6">The Parent Platform</div>
            <h2 className="font-serif-display text-4xl lg:text-5xl text-white leading-tight">
              One marketplace. <span className="gold-text italic">Five</span> ways to elevate everyday life.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7">
            <p className="text-zinc-400 text-lg leading-relaxed">
              RideXelite Services LLC is a technology-driven service marketplace connecting
              customers with vetted independent providers across five verticals. Every booking,
              every provider, every dollar — orchestrated by a single, professional platform
              designed for reliability and taste.
            </p>
          </div>
        </div>
      </section>

      {/* VERTICALS BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-32" data-testid="verticals-section">
        <div className="eyebrow mb-6">The Verticals</div>
        <motion.div
          className="grid grid-cols-1 md:grid-cols-6 gap-6 auto-rows-[280px]"
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, amount: 0.15 }}
          variants={{
            hidden: {},
            visible: { transition: { staggerChildren: 0.08, delayChildren: 0.05 } },
          }}
        >
          {VERTICALS.map((v, i) => {
            const spans = [
              "md:col-span-6",
              "md:col-span-2",
              "md:col-span-2",
              "md:col-span-2",
            ];
            const large = i === 0;
            return (
              <motion.div
                key={v.id}
                className={spans[i]}
                variants={{
                  hidden: { opacity: 0, y: 30 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
                }}
              >
                <Link
                  to={`/services/${v.id}`}
                  data-testid={`vertical-card-${v.id}`}
                  className="block h-full group relative overflow-hidden rounded-3xl border border-white/10 bg-[#121214] hover:border-[#D4AF37]/60 transition-all duration-500 hover:-translate-y-1"
                  style={{ '--v': v.color }}
                >
                  <div className="absolute inset-0">
                    <img src={v.img} alt={v.name} className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity duration-700" />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#09090B] via-[#09090B]/70 to-transparent" />
                  </div>
                  <div className="relative h-full p-8 flex flex-col justify-between">
                    <div className="flex items-start justify-between">
                      <div
                        className="w-12 h-12 rounded-2xl flex items-center justify-center"
                        style={{ background: `${v.color}40`, border: `1px solid ${v.color}` }}
                      >
                        <v.Icon weight="duotone" size={24} className="text-white" />
                      </div>
                      <span className="text-xs uppercase tracking-[0.25em] text-zinc-500 group-hover:text-[#D4AF37] transition-colors">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                    </div>
                    <div>
                      <div className="eyebrow mb-2 text-zinc-400">RideXelite</div>
                      <div className={`font-serif-display text-white ${large ? 'text-4xl lg:text-5xl' : 'text-2xl lg:text-3xl'} mb-3`}>
                        {v.name}
                      </div>
                      <div className="text-sm text-zinc-400 max-w-xs">{v.blurb}</div>
                      <div className="mt-4 inline-flex items-center gap-2 text-sm text-white group-hover:text-[#D4AF37] transition-colors">
                        Explore <ArrowRight size={14} weight="bold" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </motion.div>
      </section>

      {/* GIFT IDEAS · Diaspora → Liberia */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-24" data-testid="gift-ideas-section">
        <div className="rounded-[2rem] border border-[#D4AF37]/25 bg-gradient-to-br from-[#0F0D08] via-[#0B0B0D] to-[#0F0D08] p-8 lg:p-12 relative overflow-hidden">
          <div className="grain-overlay opacity-30" />
          <div className="relative">
            <div className="flex flex-wrap items-end justify-between gap-6 mb-8">
              <div className="flex-1 min-w-[280px]">
                <div className="eyebrow gold-text mb-3">🇱🇷 Gift a Service · Diaspora Favourite</div>
                <h2 className="font-serif-display text-3xl sm:text-4xl lg:text-5xl text-white leading-[1.05]">
                  Send love home <span className="gold-text italic">without leaving Newark</span>.
                </h2>
                <p className="mt-3 text-sm text-zinc-400 max-w-2xl">
                  Pay in USD. We'll contact your loved one in Liberia within 24 hours. One tap presets and pre-fills everything for you.
                </p>
                <GiftTicker />
              </div>
              <RotatingGiftPolaroid />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {GIFT_IDEAS.map((g) => (
                <Link
                  key={g.id}
                  to={`/book/${g.vertical}/${g.lookupKey}?gift=1`}
                  data-testid={`gift-idea-${g.id}`}
                  className="group relative rounded-2xl border border-white/10 bg-black/40 hover:border-[#D4AF37]/60 hover:bg-black/60 transition-all duration-500 p-6 flex flex-col justify-between min-h-[220px] overflow-hidden"
                >
                  <div className="absolute inset-0 opacity-20 group-hover:opacity-30 transition-opacity" style={{ background: `radial-gradient(80% 60% at 100% 0%, ${g.accent}55, transparent 70%)` }} />
                  <div className="relative">
                    <div className="w-11 h-11 rounded-2xl flex items-center justify-center mb-4" style={{ background: `${g.accent}25`, border: `1px solid ${g.accent}` }}>
                      <span className="text-2xl leading-none" aria-hidden>{g.emoji}</span>
                    </div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-zinc-500">{g.recipient}</div>
                    <div className="font-serif-display text-2xl text-white leading-tight mt-1">{g.title}</div>
                    <div className="text-xs text-zinc-400 mt-3 leading-relaxed">{g.blurb}</div>
                  </div>
                  <div className="relative flex items-end justify-between mt-6">
                    <div>
                      <div className="font-serif-display text-3xl text-white">${g.price}</div>
                      <div className="text-[10px] text-zinc-500 uppercase tracking-[0.15em]">USD · one-time</div>
                    </div>
                    <div className="inline-flex items-center gap-2 text-sm text-white group-hover:text-[#D4AF37] transition-colors">
                      Gift now <ArrowRight size={14} weight="bold" />
                    </div>
                  </div>
                </Link>
              ))}
            </div>
            <div className="mt-6 flex justify-end">
              <Link
                to="/services/home"
                className="text-xs uppercase tracking-[0.22em] text-[#D4AF37] hover:text-[#F3E5AB] transition inline-flex items-center gap-2"
                data-testid="gift-ideas-see-all"
              >
                See all services <ArrowRight size={12} weight="bold" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* PROVIDER CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="provider-cta-section">
        <div className="relative overflow-hidden rounded-[2rem] border border-white/10 bg-[#0F0F11] p-10 lg:p-16">
          <div className="grain-overlay" />
          <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <div className="eyebrow mb-6 gold-text">Independent Providers</div>
              <h3 className="font-serif-display text-4xl lg:text-5xl text-white leading-tight">
                Earn on your terms. Keep <span className="gold-text italic">80%</span> of every job.
              </h3>
              <p className="mt-6 text-zinc-400 text-lg max-w-xl">
                Sign up as an independent provider across any vertical. The platform handles
                bookings, payments, and dispatch. You focus on the work — and take home 80% of
                every completed job.
              </p>
              <Link
                to="/become-provider"
                data-testid="provider-cta-btn"
                className="mt-10 inline-flex items-center gap-2 px-8 py-4 rounded-full bg-[#D4AF37] text-black font-semibold text-sm hover:bg-[#F3E5AB] transition-colors"
              >
                Apply now <ArrowRight size={16} weight="bold" />
              </Link>
            </div>
            <div className="lg:col-span-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: "Provider payout", value: "80%" },
                  { label: "Platform fee", value: "20%" },
                  { label: "Payout timing", value: "On completion" },
                  { label: "Onboarding", value: "48 hours" },
                ].map(s => (
                  <div key={s.label} className="rounded-2xl border border-white/10 p-5 bg-black/40">
                    <div className="font-serif-display text-2xl text-white">{s.value}</div>
                    <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mt-2">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CONTACT & LOCATIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-24" data-testid="contact-locations-section" id="contact">
        <div className="eyebrow mb-6">Reach us directly</div>
        <h3 className="font-serif-display text-4xl lg:text-5xl text-white leading-tight max-w-3xl">
          Two continents. <span className="gold-text italic">One</span> standard of service.
        </h3>

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Monrovia */}
          <div className="rounded-3xl border border-white/10 bg-[#121214] p-10" data-testid="location-monrovia">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow gold-text mb-2">Monrovia, Liberia</div>
                <div className="font-serif-display text-3xl text-white">Call / WhatsApp Ralph</div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <Phone weight="duotone" size={22} className="text-[#D4AF37]" />
              </div>
            </div>
            <a
              href="tel:+231777689123"
              data-testid="liberia-booking-phone"
              className="mt-8 block rounded-2xl border border-[#D4AF37]/30 bg-black/40 p-8 hover:bg-black/60 transition"
            >
              <div className="eyebrow text-zinc-400">Liberia bookings</div>
              <div className="font-serif-display text-4xl text-white mt-2">+231 77 768 9123</div>
            </a>
            <a
              href="https://wa.me/231777689123"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="liberia-booking-whatsapp"
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#25D366] hover:text-[#4be089] transition"
            >
              WhatsApp Ralph <ArrowRight size={12} weight="bold" />
            </a>
            <div className="mt-8 flex items-center gap-2 text-sm text-zinc-400">
              <MapPin size={16} weight="duotone" className="text-[#D4AF37]" /> Marshall, Margibi, Liberia
            </div>
          </div>

          {/* US */}
          <div className="rounded-3xl border border-[#D4AF37]/40 bg-gradient-to-br from-[#121214] to-[#0F0F11] p-10" data-testid="location-us">
            <div className="flex items-start justify-between">
              <div>
                <div className="eyebrow gold-text mb-2">United States</div>
                <div className="font-serif-display text-3xl text-white">Call / WhatsApp Charles</div>
              </div>
              <div className="w-12 h-12 rounded-2xl bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center">
                <Phone weight="duotone" size={22} className="text-[#D4AF37]" />
              </div>
            </div>
            <a
              href="tel:+14436836392"
              data-testid="us-booking-phone"
              className="mt-8 block rounded-2xl border border-[#D4AF37]/30 bg-black/40 p-8 hover:bg-black/60 transition"
            >
              <div className="eyebrow text-zinc-400">USA bookings</div>
              <div className="font-serif-display text-4xl text-white mt-2">443-683-6392</div>
            </a>
            <a
              href="https://wa.me/14436836392"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="us-booking-whatsapp"
              className="mt-3 inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] text-[#25D366] hover:text-[#4be089] transition"
            >
              WhatsApp Charles <ArrowRight size={12} weight="bold" />
            </a>
            <div className="mt-6 grid grid-cols-1 gap-3 text-sm">
              <a href="mailto:support@ridexelite.com" className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37] transition">
                <EnvelopeSimple size={16} weight="duotone" /> support@ridexelite.com
              </a>
              <a href="mailto:ridexafrica@outlook.com" className="flex items-center gap-3 text-zinc-300 hover:text-[#D4AF37] transition">
                <EnvelopeSimple size={16} weight="duotone" /> ridexafrica@outlook.com
              </a>
            </div>
            <div className="mt-8 inline-block px-4 py-2 rounded-full border border-[#D4AF37]/30 bg-[#D4AF37]/5 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
              Driven by Excellence · Built on Trust · Designed for You
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
